import { Suspense, useEffect, useState } from 'react';
import { lazyRoute } from '@/routing/lazyRoute';
import { GATE_PENDING_SELECTOR } from './gatePendingSelector';

const GatePendingStudies = lazyRoute(
  () => import('./GatePendingStudies'),
  (module) => module.GatePendingStudies,
);

function holdsPending(node: Node): boolean {
  return (
    node instanceof Element &&
    (node.matches(GATE_PENDING_SELECTOR) || node.querySelector(GATE_PENDING_SELECTOR) !== null)
  );
}

/**
 * F-0578 WAIT-14, T-0559: `GatePendingStudies` is not in the launch's entry. This watches the page
 * for the first control still checking access and only then loads it; it scans what is already
 * pending when it mounts, so a study is drawn the same `PENDING_INDICATOR_DELAY_MS` after the chunk
 * arrives. A launch whose access answers come first never fetches it.
 *
 * *Mutation:* render `GatePendingStudies` here unconditionally from a static import → the entry
 * grows by its bytes and `launch-tab-boot.spec.ts` is over its size.
 */
export function GatePendingStudiesOnDemand() {
  const [needed, setNeeded] = useState(false);

  useEffect(() => {
    if (needed || typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return undefined;
    }
    const host = document.body;
    if (holdsPending(host)) {
      setNeeded(true);
      return undefined;
    }
    const observer = new MutationObserver((records) => {
      const found = records.some((record) =>
        record.type === 'attributes'
          ? (record.target as Element).matches(GATE_PENDING_SELECTOR)
          : [...record.addedNodes].some(holdsPending),
      );
      if (!found) return;
      observer.disconnect();
      setNeeded(true);
    });
    observer.observe(host, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-busy', 'aria-disabled'],
    });
    return () => observer.disconnect();
  }, [needed]);

  if (!needed) return null;
  return (
    <Suspense fallback={null}>
      <GatePendingStudies />
    </Suspense>
  );
}
